/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/react'
import { MouseEvent, Fragment, useMemo, useState } from 'react'
import { Box, FormControlLabel, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material'
import Close from '@mui/icons-material/Close'
import ViewColumn from '@mui/icons-material/ViewColumn'
import { nanoid } from 'nanoid'
import { ColumnInstance } from 'react-table'
import { SimpleMap } from '@vendorflow/types'
import { MobilePopoverStyling } from './Toolbar'
import { ACTION_COLUMN_ID } from '../DataTable'
import IndeterminateCheckbox from './IndeterminateCheckbox'

interface Props<D extends object> {
  allColumns: ColumnInstance<D>[]
  columnNameById: SimpleMap<string>
}

export default function ViewColumnTool<D extends object>({ allColumns, columnNameById }: Props<D>) {
  const id = useMemo(() => nanoid(), [])
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const open = !!anchorEl

  function handleClick(evt: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(evt.currentTarget)
  }

  function handleClose() {
    setAnchorEl(null)
  }

  return (
    <Fragment>
      <Tooltip title="Show Columns">
        <IconButton aria-describedby={id} onClick={handleClick}>
          <ViewColumn />
        </IconButton>
      </Tooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box css={MobilePopoverStyling} boxSizing="border-box" padding="1rem">
          <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.54)' }}>Show Columns</Typography>
            <Tooltip title="Close">
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction="column" justifyContent="flex-start" alignItems="flex-start">
            {allColumns
              .filter((column) => column.id !== ACTION_COLUMN_ID)
              .map((column) => {
                return (
                  <FormControlLabel
                    key={column.id}
                    label={columnNameById[column.id]}
                    control={<IndeterminateCheckbox {...column.getToggleHiddenProps()} />}
                  />
                )
              })}
          </Stack>
        </Box>
      </Popover>
    </Fragment>
  )
}
